import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { BillingStrategy, RetailCustomerConfiguration, RetailCustomerConfigurationService } from '@galaxy/billing';
import { LineItemCacheService } from '@galaxy/inventory-ui';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { CommonProperties, ProductAnalyticsService } from '@vendasta/product-analytics';
import { DurationInterface, Order, Origin, Order as SalesOrder, SalesOrdersService } from '@vendasta/sales-orders';
import { Breadcrumbs } from '@vendasta/uikit';
import { BehaviorSubject, EMPTY, Observable, Subscription, combineLatest, of } from 'rxjs';
import {
  bufferTime,
  catchError,
  filter,
  finalize,
  first,
  map,
  shareReplay,
  switchMap,
  take,
  tap,
} from 'rxjs/operators';
import { AccountService } from '../../../core/account.service';
import { GoogleWorkspaceAppIds, Office365AppIds } from '../../../marketplace-app/global-brands-constants';
import { ActivationSettingsApiService } from '../../activation-settings-api.service';
import { ActivateAppService } from '../../core/activate-app/activate-app.service';
import { ActivationNavigationService } from '../../core/activation-navigation.service';
import { ActivationParamService } from '../../core/activation-param.service';
import { ActivationService } from '../../core/activation.service';
import { ActivatableItem, AppIdToEditionIdToEditionMap } from '../../core/interface';
import {
  ActivationMessageDialogComponent,
  ActivationMessageDialogData,
  ActivationMessageSupportedApps,
} from '../activation-message-dialog/activation-message-dialog.component';
import { OrderConfirmationItem } from '../interface';
import { OrderConfirmationService } from '../order-confirmation.service';
import { OrderSubmission } from '../order-summary/order-summary.component';
import { StairstepOrderService } from '../stairstep-order.service';
import { AccountGroup } from '@galaxy/account-group';

@Component({
  selector: 'app-order-confirmation-page',
  templateUrl: './order-confirmation-page.component.html',
  standalone: false,
})
export class OrderConfirmationPageComponent implements OnInit, OnDestroy {
  constructor(
    private productConfirmationService: OrderConfirmationService,
    private paramService: ActivationParamService,
    private snackbarService: SnackbarService,
    private activateAppService: ActivateAppService,
    private dialog: MatDialog,
    private activationService: ActivationService,
    private activationSettingsApiService: ActivationSettingsApiService,
    private activationNavigationService: ActivationNavigationService,
    private salesOrdersService: SalesOrdersService,
    private stairstepOrderService: StairstepOrderService,
    private accountService: AccountService,
    private analytics: ProductAnalyticsService,
    private readonly customerConfigService: RetailCustomerConfigurationService,
    private lineItemCacheService: LineItemCacheService,
  ) {}

  orderConfirmationItems$: Observable<OrderConfirmationItem[]>;
  hidePricing$: Observable<boolean>;
  productIsInstantBilledMap$: Observable<Map<string, BillingStrategy>>;
  userCanEditBillingInfo$: Observable<boolean>;
  billingInfoConfigured$: Observable<boolean>;
  stairstepOrderRequiresBilling$: Observable<boolean>;
  partnerId$: Observable<string>;
  partnerId: string;
  accountGroupId$: Observable<string>;
  accountGroupId: string;
  editionsMap$: Observable<AppIdToEditionIdToEditionMap>;
  purchased: boolean;
  numOrderItems: number;
  billingAutomationEnabled$: Observable<boolean>;
  marketCurrency$: Observable<string>;

  salesOrder$: Observable<SalesOrder>;
  business$: Observable<AccountGroup>;

  disableSubmitButton$$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  breadcrumbs: Breadcrumbs[];
  private subscriptions: Subscription[] = [];
  private activationMessageDialogRef: MatDialogRef<ActivationMessageDialogComponent>;
  private activatableItems$: Observable<ActivatableItem[]>;

  requireApproval = false;
  approvalFromSubjectId = '';
  hasExpiryDate = false;
  expiryDate = new Date();

  /**
   * Constructs a list of products IDs from an order.
   * @param order - the sales order to get product IDs for
   */
  static buildProductIdListFromSalesOrder(order: SalesOrder): string[] {
    return (
      order?.lineItems
        ?.map((li) => {
          return li.appKey?.appId;
        })
        ?.filter(Boolean) ?? []
    );
  }

  ngOnInit(): void {
    this.paramService.accountGroupId$.pipe(take(1)).subscribe((agid) => this.accountService.setCurrentAccountID(agid));
    this.orderConfirmationItems$ = this.productConfirmationService.orderConfirmationItems$;
    this.hidePricing$ = this.productConfirmationService.hidePricing$;
    this.productIsInstantBilledMap$ = this.productConfirmationService.productIsInstantBilledMap$;
    this.userCanEditBillingInfo$ = this.productConfirmationService.userCanEditBillingInfo$;
    this.billingInfoConfigured$ = this.productConfirmationService.billingInfoConfigured$;
    this.stairstepOrderRequiresBilling$ = this.stairstepOrderService.orderRequiresBillingInfo(
      this.orderConfirmationItems$,
    );

    this.partnerId$ = this.paramService.partnerId$;
    this.subscriptions.push(this.partnerId$.subscribe((pid) => (this.partnerId = pid)));
    this.accountGroupId$ = this.paramService.accountGroupId$;
    this.marketCurrency$ = this.productConfirmationService.marketCurrency$;
    this.subscriptions.push(this.accountGroupId$.subscribe((agid) => (this.accountGroupId = agid)));
    this.subscriptions.push(this.orderConfirmationItems$.subscribe((ocis) => (this.numOrderItems = ocis.length)));

    this.activatableItems$ = this.activationService.unfilteredFlattenedActivatableItems$;

    this.editionsMap$ = this.productConfirmationService.editionIdToEditionMap$;

    this.salesOrder$ = this.productConfirmationService.salesOrder$;
    this.business$ = this.productConfirmationService.business$;

    // Listen for and display responses from activation
    this.subscriptions.push(
      this.activateAppService.successes$
        .pipe(
          bufferTime(1000),
          filter((successResults) => {
            return successResults && successResults.length > 0;
          }),
        )
        .subscribe((successResults) => {
          let message = (successResults || []).reduce((msg, curr) => {
            if (msg) {
              return `${msg} and ${curr.name}`;
            }
            return `${curr.name}`;
          }, '');
          message = `${message} activated successfully`;
          this.snackbarService.openSuccessSnack(message);
        }),
    );

    this.subscriptions.push(
      this.activateAppService.errors$
        .pipe(
          bufferTime(1000),
          filter((errorResults) => {
            return errorResults && errorResults.length > 0;
          }),
        )
        .subscribe((errorResults) => {
          let message = (errorResults || []).reduce((msg, curr) => {
            if (msg) {
              return `${msg} and ${curr.name}`;
            }
            return `${curr.name}`;
          }, '');
          message = `${message} failed activation`;
          this.snackbarService.openErrorSnack(message);
        }),
    );
    const retailConfig$ = this.customerConfigService.get(this.partnerId, this.accountGroupId).pipe(
      take(1),
      catchError((err) => {
        if (err.status === 404) {
          const retailConfig: RetailCustomerConfiguration = {
            merchantId: this.partnerId,
            customerId: this.accountGroupId,
            contactId: '',
          };
          return of(retailConfig);
        }
      }),
      shareReplay(1),
    );
    this.billingAutomationEnabled$ = retailConfig$.pipe(
      map((retailConfig) => {
        return retailConfig.autoGenerate && !retailConfig.useTemplateInvoice;
      }),
    );

    const billingUserId$ = retailConfig$.pipe(map((config) => (this.approvalFromSubjectId = config.contactId)));
    combineLatest([billingUserId$, this.accountService.users$])
      .pipe(
        filter(([_, users]) => !!users),
        take(1),
        map(([userId, users]) => users.find((user) => user.unifiedUserId === userId)),
        map((user) => user?.userId || ''),
      )
      .subscribe((subjectId) => (this.approvalFromSubjectId = subjectId));

    this.trackOrderFormStatus('order-confirmation-page-checkout-initiated');
  }

  /**
   * The function called when the submit button is clicked.
   *
   * @param event {OrderSubmission} An optional parameter for triggering email sending
   *
   */
  onSubmitClicked(event?: OrderSubmission): void {
    this.disableSubmitButton$$.next(true);

    const processOrder$ = this.billingAutomationEnabled$.pipe(
      map((billingAutomationEnabled) =>
        // You must have the feature flag enabled, billing automation enabled and the user must have specified that an approval is required to send to the customer.
        billingAutomationEnabled && this.requireApproval ? this.approvalFromSubjectId : null,
      ),
      switchMap((subjectId) =>
        this.createAndActivateOrSendToCustomer(
          subjectId,
          this.requireApproval && this.hasExpiryDate ? this.expiryDate : null,
          event.order,
        ).pipe(
          catchError(() => {
            this.snackbarService.openErrorSnack('SALES_ORDERS.ERRORS.ERROR_CREATE_ACTIVATION_ORDER');
            this.disableSubmitButton$$.next(false);
            return EMPTY;
          }),
          tap((_) => {
            this.purchased = true;
            if (!!subjectId && this.requireApproval) {
              this.analytics.trackEvent('send-order-for-approval', 'activate-products', 'purchase');
              this.snackbarService.openSuccessSnack('SALES_ORDERS.SUCCESS.SENT_FOR_APPROVAL');
            } else {
              this.analytics.trackEvent('create-order', 'activate-products', 'purchase');
              this.snackbarService.openSuccessSnack('SALES_ORDERS.SUCCESS.CREATE_ACTIVATION_ORDER');
            }
          }),
        ),
      ),
    );

    combineLatest([processOrder$, this.accountGroupId$])
      .pipe(
        take(1),
        switchMap(([orderId, businessId]) => {
          return combineLatest([
            this.salesOrdersService.get(orderId, businessId),
            this.activatableItems$,
            of(businessId),
          ]).pipe(take(1));
        }),
        // Track whether the cart had items with an order form.
        tap(() => this.trackOrderFormStatus('order-confirmation-page-checkout-finalized')),
        // Clear the cart and notify listeners
        tap(([order, activatableItems, businessId]) => {
          this.productConfirmationService.clearSalesOrderCache();
          this.lineItemCacheService.clearLineItemCache(businessId);
          this.activationSuccessfulAction(order, activatableItems, event.sendEmail);
        }),
        finalize(() => this.disableSubmitButton$$.next(false)),
      )
      .subscribe();
  }

  /**
   * Tracks whether the order currently has a product with an order form in it.
   * @param label The label to add to the event sent to the tracker.
   * @private
   */
  private trackOrderFormStatus(label: string): void {
    // Determine whether the order had an order form.
    this.activationService.appCartItems$.pipe(take(1)).subscribe((appCartItems) => {
      this.analytics.trackEvent(label, CommonProperties.ECOMMERCE, 'has-matching-item', 0, {
        hasAppWithOrderForm: !!appCartItems?.find((item) => item.activatableItem.usesOrderForm),
      });
    });
  }

  /**
   * Set order.origin to PC then create order
   * and send for customer approval
   * sales-orders API defaults to `Origin.SSC` if origin is not set.
   *
   * @param order
   * @param userId
   * @param expiry is when the offer sent to the user expires.
   */
  createAndSendToCustomer(order, userId, expiry: Date): Observable<string> {
    order.origin = Origin.PC;
    return this.salesOrdersService.createAndSendToCustomer(order, userId, expiry);
  }

  /**
   * Creates auto-activatable order to activate products
   * or create and send to customer for approval if there is a userId.
   */
  createAndActivateOrSendToCustomer(userId: string, expiry: Date, order: Order): Observable<string> {
    if (this.requireApproval) {
      order.orderIsSmbPayable = true;
      return this.createAndSendToCustomer(order, userId, expiry);
    }
    return this.salesOrdersService.createAndActivateOrder(order);
  }

  /**
   * Notifies the user that the order was submitted successfully.
   *
   * If the order contained products which should display a post-activation dialog, the dialog is displayed.
   * Otherwise, an order successful toast message is displayed and the user is redirected to the account details screen
   */
  activationSuccessfulAction(
    order: SalesOrder,
    activatableItems: ActivatableItem[],
    sendEmailNotification = false,
  ): void {
    const activationMessageDialogData: ActivationMessageDialogData = {
      apps: [],
    };

    // TODO - should both products and packages be sent for activation emails?
    const productIds = OrderConfirmationPageComponent.buildProductIdListFromSalesOrder(order);
    if (productIds?.length > 0) {
      this.activationSettingsApiService
        .setNotificationSettings(order.businessId, productIds, sendEmailNotification)
        .pipe(first())
        .subscribe();
    }

    const hasGoogleWorkspaceItem = (appID) => GoogleWorkspaceAppIds.some((id) => id === appID);
    const googleWorkspaceItem = order.lineItems.find((a) => hasGoogleWorkspaceItem(a.appKey?.appId));
    const googleWorkspaceActivatableItem = (activatableItems || []).find(
      (a) => a.appId === googleWorkspaceItem?.appKey?.appId,
    );
    if (googleWorkspaceActivatableItem) {
      activationMessageDialogData.apps.push({
        name: googleWorkspaceActivatableItem.name,
        appId: googleWorkspaceActivatableItem.appId,
        icon: googleWorkspaceActivatableItem.icon || '',
        type: ActivationMessageSupportedApps.WORKSPACE,
      });
    }

    const hasOffice365Item = (appID) => Office365AppIds.some((id) => id === appID);
    const office365Item = order.lineItems.find((a) => hasOffice365Item(a.appKey?.appId));
    const office365ItemActivatableItem = (activatableItems || []).find((a) => a.appId === office365Item?.appKey?.appId);
    if (office365ItemActivatableItem) {
      activationMessageDialogData.apps.push({
        name: office365ItemActivatableItem.name,
        appId: office365ItemActivatableItem.appId,
        icon: office365ItemActivatableItem.icon || '',
        type: ActivationMessageSupportedApps.OFFICE,
      });
    }

    if (activationMessageDialogData.apps.length > 0) {
      this.activationMessageDialog(activationMessageDialogData);
    } else {
      this.activationNavigationService.navigateToNextStep('confirmation', order);
    }
  }

  activationMessageDialog(apps: ActivationMessageDialogData): void {
    this.activationMessageDialogRef = this.dialog.open(ActivationMessageDialogComponent, {
      width: '622px',
      data: apps,
    });
    this.activationMessageDialogRef.afterClosed().subscribe(() => {
      this.activationNavigationService.navigateToNextStep('confirmation');
    });
  }

  goBack(): void {
    this.activationNavigationService.navigateToPreviousStep('confirmation');
  }

  setContractStartDate(startDate: Date): void {
    this.productConfirmationService.setContractStartDate(startDate);
  }

  setContractDuration(contractDuration: DurationInterface): void {
    this.productConfirmationService.setContractDuration(contractDuration);
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }
}
